import { useState } from "react"
import { toast } from "sonner"

import { useAliases, useCrearPrecio, usePrecios } from "@/api/queries"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatearFecha } from "@/shared/format"

type Borrador = { alias: string; in_uncached: string; in_cached: string; out: string }

const VACIO: Borrador = { alias: "", in_uncached: "", in_cached: "", out: "" }

export function PricesPage() {
  const precios = usePrecios()
  const aliases = useAliases()
  const crear = useCrearPrecio()

  const [borrador, setBorrador] = useState<Borrador | null>(null)

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    if (borrador === null) return
    try {
      await crear.mutateAsync({
        alias: borrador.alias,
        unit: "tokens",
        in_uncached: borrador.in_uncached,
        in_cached: borrador.in_cached,
        out: borrador.out,
        valid_from: null,
      })
      toast.success("Precio creado", {
        description: "El anterior queda cerrado; el consumo pasado conserva su precio.",
      })
      setBorrador(null)
    } catch {
      toast.error("No se pudo crear el precio")
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Precios</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Por millón de tokens. Un precio nuevo <strong>cierra</strong> el anterior: el
            consumo pasado se sigue cobrando al precio que estaba vigente entonces.
          </p>
        </div>
        <Button onClick={() => setBorrador({ ...VACIO })}>Nuevo precio</Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {precios.isPending ? (
            <Skeleton className="h-40" />
          ) : precios.isError ? (
            <p className="text-sm text-destructive">No se pudieron cargar los precios.</p>
          ) : precios.data.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Todavía no hay precios. Los alias sin precio no despachan.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Alias</TableHead>
                    <TableHead className="text-right">Input</TableHead>
                    <TableHead className="text-right">Input cacheado</TableHead>
                    <TableHead className="text-right">Output</TableHead>
                    <TableHead>Verificado</TableHead>
                    <TableHead>Vigencia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {precios.data.map((p) => (
                    <TableRow key={p.id} className={p.vigente ? undefined : "opacity-60"}>
                      <TableCell className="font-medium">{p.alias}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.in_uncached}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.in_cached}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.out}</TableCell>
                      <TableCell className="text-sm">
                        {/* "sin verificar" no es un hueco vacío a propósito: cuando el
                            cuadre avise de una desviación, lo primero que hay que saber
                            es contra qué tarifa se comparó y desde cuándo. */}
                        {p.verified_on === null ? (
                          <span className="text-muted-foreground">sin verificar</span>
                        ) : (
                          <span title={p.source ?? undefined} className="tabular-nums">
                            {p.verified_on}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.vigente ? (
                          <Badge variant="secondary">vigente</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            hasta {formatearFecha(p.valid_to)}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={borrador !== null} onOpenChange={(abierto) => !abierto && setBorrador(null)}>
        <DialogContent>
          <form onSubmit={enviar}>
            <DialogHeader>
              <DialogTitle>Nuevo precio</DialogTitle>
              <DialogDescription>
                Importes por millón de tokens, como texto. Verifícalos contra la página de
                tarifas del proveedor: un precio desactualizado no rompe nada visible,
                factura mal en silencio.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="alias-precio">Alias</Label>
                <Select
                  value={borrador?.alias ?? ""}
                  onValueChange={(v) =>
                    setBorrador((b) => (b === null ? b : { ...b, alias: v }))
                  }
                >
                  <SelectTrigger id="alias-precio">
                    <SelectValue placeholder="Elige un alias" />
                  </SelectTrigger>
                  <SelectContent>
                    {(aliases.data ?? []).map((a) => (
                      <SelectItem key={a.alias} value={a.alias}>
                        {a.alias}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="in-uncached">Input</Label>
                  <Input
                    id="in-uncached"
                    inputMode="decimal"
                    placeholder="0.27"
                    value={borrador?.in_uncached ?? ""}
                    onChange={(e) =>
                      setBorrador((b) => (b === null ? b : { ...b, in_uncached: e.target.value }))
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="in-cached">Cacheado</Label>
                  <Input
                    id="in-cached"
                    inputMode="decimal"
                    placeholder="0.07"
                    value={borrador?.in_cached ?? ""}
                    onChange={(e) =>
                      setBorrador((b) => (b === null ? b : { ...b, in_cached: e.target.value }))
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="out">Output</Label>
                  <Input
                    id="out"
                    inputMode="decimal"
                    placeholder="1.10"
                    value={borrador?.out ?? ""}
                    onChange={(e) =>
                      setBorrador((b) => (b === null ? b : { ...b, out: e.target.value }))
                    }
                    required
                  />
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                El input cacheado suele costar bastante menos. Si el proveedor no distingue,
                pon el mismo valor en ambos.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBorrador(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={crear.isPending || borrador?.alias === ""}>
                {crear.isPending ? "Creando…" : "Crear precio"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
